// handle access and refresh tokens, setting, getting, deleting, and refreshing

import axios from 'axios';
import { defaultConfig, apiRoutes } from './apiConfig';
import { CONNECTION_STATES } from '../contexts/authConstants';
import { userStorage } from '../utils/userStorage';
import {
    clearStaleLock,
    isOtherTabRefreshing,
    tryAcquireLock,
    waitForOtherTabRefresh,
    broadcastRefreshDone,
    broadcastRefreshFailed,
    releaseLock
} from './authRefreshCoordinator';

const FOLLOWER_TIMEOUT_MESSAGE = 'Token refresh timeout';

// Add refresh promise for deduplication
let refreshPromise = null;

// store access token and expires at in memory only
let accessToken = null;
let accessTokenExpiresAt = null;
// Timer for proactive (background) token refresh, scheduled before expiry
let proactiveRefreshTimer = null;

// Create a separate api instance for token refresh
const tokenApi = axios.create(defaultConfig);

/**
 * Schedules a silent background token refresh 60 seconds before the access
 * token expires. Cancels any previously scheduled timer first.
 * Only schedules if we have an expiry time and we know we're online.
 */
function scheduleProactiveRefresh() {
    // Clear any existing scheduled timer
    if (proactiveRefreshTimer !== null) {
        clearTimeout(proactiveRefreshTimer);
        proactiveRefreshTimer = null;
    }

    if (!accessTokenExpiresAt) return;

    // Refresh 60 seconds before the token expires (or immediately if < 60s remaining)
    const msUntilRefresh = Math.max(0, accessTokenExpiresAt - Date.now() - 60_000);

    // Don't schedule if the token is already expired or expiring within 2 seconds
    // (the reactive 401 flow will handle those cases)
    if (msUntilRefresh < 2000) return;

    // console.log(`[tokenService] Proactive refresh scheduled in ${Math.round(msUntilRefresh / 1000)}s`);

    proactiveRefreshTimer = setTimeout(() => {
        proactiveRefreshTimer = null;
        // Only refresh if we're online and the token hasn't been refreshed already
        if (!navigator.onLine) return;
        if (!accessToken) return; // User logged out

        // console.log('[tokenService] Proactive refresh: starting background refresh...');
        tokenService.refreshAccessToken().then(() => {
            // console.log('[tokenService] Proactive refresh: completed successfully');
        }).catch((err) => {
            // Silently swallow errors — the reactive 401 flow is the safety net
            console.warn('[tokenService] Proactive refresh failed (reactive flow will handle it):', err.message);
        });
    }, msUntilRefresh);
}

const tokenService = {
    setAccessToken: (token, expiresAt = null, options = {}) => {
        accessTokenExpiresAt = expiresAt != null ? expiresAt : Date.now() + 15 * 60 * 1000;
        accessToken = token;
        // Schedule a background refresh before the token expires (skip when login-as-user token)
        if (!options.skipProactiveRefresh) {
            scheduleProactiveRefresh();
        }
    },
    getAccessToken: () => {
        return accessToken;
    },
    getAccessTokenExpiresAt: () => accessTokenExpiresAt,
    deleteAccessToken: () => {
        accessToken = null;
        accessTokenExpiresAt = null;
        // Cancel any pending proactive refresh (user logged out)
        if (proactiveRefreshTimer !== null) {
            clearTimeout(proactiveRefreshTimer);
            proactiveRefreshTimer = null;
        }
    },
    isAccessTokenValid: () => {
        // If we're offline and have a token, consider it valid regardless of expiration
        // This allows the app to work offline even with an expired token
        if (!navigator.onLine && accessToken) {
            return true;
        }
        const isValid = accessToken && accessTokenExpiresAt && Date.now() < parseInt(accessTokenExpiresAt);
        return isValid;
    },
    setRefreshTokenAndClientKey: async (refreshToken, clientKey) => {
        try {
            await userStorage.setRefreshTokenAndClientKey(refreshToken, clientKey);
        } catch (error) {
            console.error('Failed to set refresh token and client key:', error);
            throw error;
        }
    },
    getRefreshTokenAndKey: async () => {
        try {
            const tokenData = await userStorage.getRefreshTokenAndClientKey();
            if (!tokenData) {
                return null;
            }
            return tokenData;
        } catch (error) {
            console.error('Error getting refresh token and key:', error);
            return null;
        }
    },
    deleteRefreshTokenAndKey: () => {
        try {
            userStorage.clearUserData(true);
        } catch (error) {
            console.error('Error deleting refresh token and key:', error);
        }
    },
    hasRefreshToken: async () => {
        try {
            const tokenData = await tokenService.getRefreshTokenAndKey();
            // Safely check if tokenData exists and has the required properties
            const hasToken = !!(tokenData && tokenData.refreshToken && tokenData.clientKey);
            return hasToken;
        } catch (error) {
            console.error('Error checking refresh token:', error);
            return false;
        }
    },
    refreshAccessToken: async () => {
        // If there's already a refresh in progress, return that promise
        if (refreshPromise) {
            return refreshPromise;
        }

        // If we're offline, immediately return an offline response
        if (!navigator.onLine) {
            return {
                offline: true,
                message: 'Device is offline, using cached credentials'
            };
        }

        try {
            refreshPromise = (async () => {
                const tokenData = await tokenService.getRefreshTokenAndKey();
                if (!tokenData) throw new Error('No token data available');

                const { refreshToken, clientKey } = tokenData;
                if (!refreshToken) throw new Error('No refresh token available');
                if (!clientKey) throw new Error('No client key available');

                // Clear lock from a tab that closed so we don't wait forever
                clearStaleLock();

                const applyPayload = async (payload) => {
                    tokenService.setAccessToken(payload.accessToken, payload.accessTokenExpiresAt);
                    if (payload.user) {
                        await userStorage.setUserData(payload.user, true);
                        window.dispatchEvent(new CustomEvent('user-data-updated', { detail: { userData: payload.user } }));
                    }
                    return { access_token: payload.accessToken, user: payload.user };
                };

                const waitOrBecomeLeader = async () => {
                    if (isOtherTabRefreshing()) {
                        try {
                            return { kind: 'payload', payload: await waitForOtherTabRefresh() };
                        } catch (e) {
                            if (e.message === FOLLOWER_TIMEOUT_MESSAGE) {
                                releaseLock();
                                return { kind: 'retry' };
                            }
                            throw e;
                        }
                    }
                    const { acquired } = await tryAcquireLock();
                    if (acquired) return { kind: 'leader' };
                    try {
                        return { kind: 'payload', payload: await waitForOtherTabRefresh() };
                    } catch (e) {
                        if (e.message === FOLLOWER_TIMEOUT_MESSAGE) {
                            releaseLock();
                            return { kind: 'retry' };
                        }
                        throw e;
                    }
                };

                let step = await waitOrBecomeLeader();
                if (step.kind === 'retry') step = await waitOrBecomeLeader();
                if (step.kind === 'payload') return await applyPayload(step.payload);
                if (step.kind !== 'leader') throw new Error(FOLLOWER_TIMEOUT_MESSAGE);

                try {
                    // Create a timeout promise to abort if taking too long
                    const timeoutPromise = new Promise((_, reject) => {
                        setTimeout(() => reject(new Error('Token refresh timeout')), 10000);
                    });

                    // Race the actual request against the timeout
                    const response = await Promise.race([
                        tokenApi.post(apiRoutes.auth.refreshToken.path, {
                            refresh_token: refreshToken,
                            client_key: clientKey
                        }),
                        timeoutPromise
                    ]);

                    const { access_token, access_token_expires_at, refresh_token, user } = response.data;

                    // Persist new refresh token first so a reload doesn't lose the session
                    await tokenService.setRefreshTokenAndClientKey(refresh_token, clientKey);
                    // Pass the server-provided expiry time so client clock drift doesn't matter.
                    // Falls back to null (which setAccessToken will default to +15min from now).
                    tokenService.setAccessToken(access_token, access_token_expires_at ?? null);

                    // If we got user data in the response, update it
                    if (user) {
                        await userStorage.setUserData(user, true); // Skip event dispatch
                        window.dispatchEvent(new CustomEvent('user-data-updated', {
                            detail: { userData: user }
                        }));
                    }

                    // Tell other tabs so they don't send a second request with the same (now invalid) token
                    broadcastRefreshDone({
                        accessToken: access_token,
                        accessTokenExpiresAt: tokenService.getAccessTokenExpiresAt(),
                        user: user ?? undefined
                    });

                    return response.data;
                } catch (error) {
                    // Handle network errors by setting offline state
                    if (!navigator.onLine || error.message?.includes('Network Error') ||
                        error.message?.includes('timeout') || error.code === 'ECONNABORTED') {
                        localStorage.setItem('connection_state', CONNECTION_STATES.OFFLINE);
                        window.dispatchEvent(new CustomEvent('connection-state-change', {
                            detail: { state: CONNECTION_STATES.OFFLINE }
                        }));
                        return {
                            offline: true,
                            message: 'Device is offline, using cached credentials'
                        };
                    }

                    broadcastRefreshFailed();
                    throw error;
                }
            })();
            return await refreshPromise;
        } catch (error) {
            console.error('Token refresh failed:', error);
            throw error;
        } finally {
            refreshPromise = null;
        }
    }
};

export default tokenService;       